import React, { forwardRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { BrandLockup } from '@/components/Brand';
import ThemeToggle from '@/components/ThemeToggle';
import { severityMeta } from '@/lib/severity';

/**
 * Shared split-screen authentication layout and the form vocabulary used
 * by every sign-in surface (login, register, staff sign-in).
 *
 *   <AuthSplit>
 *     <AuthSplit.Brief>...editorial left panel...</AuthSplit.Brief>
 *     <AuthSplit.Panel backTo="/">...form...</AuthSplit.Panel>
 *   </AuthSplit>
 *
 * Below 960px the brief is hidden and the form is the first thing on the
 * page (styles/welcome.css, "Split auth layout").
 */

function AuthSplit({ children }) {
  return <div className="auth-split">{children}</div>;
}

/**
 * Editorial context panel. Rows are illustrative, so the card title must
 * say "Sample" and nothing in it animates or ticks like live data.
 */
function BriefSidebar({
  sectionLabel = 'Morning Brief',
  headline,
  emphasis,
  briefTitle = 'Active Campaigns · Sample',
  rows = [],
  quote,
}) {
  return (
    <aside className="auth-brief is-blueprint-bg is-grain" aria-label="About IntelliSecure">
      <Link to="/" style={{ textDecoration: 'none', alignSelf: 'flex-start' }} aria-label="IntelliSecure home">
        <BrandLockup size={26} subtitle="Threat Intelligence" />
      </Link>

      <div className="auth-brief-body">
        <div className="auth-eyebrow" style={{ marginBottom: 0 }}>{sectionLabel}</div>

        <h2>
          {headline}{' '}
          {emphasis && <em>{emphasis}</em>}
        </h2>

        {rows.length > 0 && (
          <div className="auth-brief-card is-framed">
            <div className="auth-brief-card-head t-label">{briefTitle}</div>
            {rows.map((c) => {
              const sev = severityMeta(c.sev);
              return (
                <div key={c.serial} className="auth-brief-row">
                  <span className="bar" style={{ background: sev.color }} aria-hidden="true" />
                  <div>
                    <div className="name">{c.name}</div>
                    <div className="meta">{sev.label} · {c.meta}</div>
                  </div>
                  <span className="is-serial">{c.serial}</span>
                </div>
              );
            })}
            <span className="is-frame-tr" />
            <span className="is-frame-br" />
          </div>
        )}

        {quote && <p className="auth-brief-quote">{quote}</p>}
      </div>

      {/* Controls that exist in the product today; no certifications claimed. */}
      <div className="auth-brief-rail t-label">
        <span>MFA</span>
        <span className="sep" aria-hidden="true">·</span>
        <span>Audit trail</span>
        <span className="sep" aria-hidden="true">·</span>
        <span>Verified domains</span>
      </div>
    </aside>
  );
}

function BackControl({ backTo, onBack, backLabel }) {
  if (backTo) {
    return (
      <Link to={backTo} className="auth-back" data-testid="auth-back-btn">
        <ArrowLeft size={12} aria-hidden="true" /> {backLabel}
      </Link>
    );
  }
  if (!onBack) return null;
  return (
    <button type="button" onClick={onBack} className="auth-back" data-testid="auth-back-btn">
      <ArrowLeft size={12} aria-hidden="true" /> {backLabel}
    </button>
  );
}

function AuthPanel({ children, onBack, backTo, backLabel = 'Back' }) {
  return (
    <div className="auth-panel">
      <div className="auth-panel-top">
        <Link to="/" className="auth-panel-brand" aria-label="IntelliSecure home">
          <BrandLockup size={24} />
        </Link>
        <BackControl backTo={backTo} onBack={onBack} backLabel={backLabel} />
        <div style={{ flex: 1 }} />
        <ThemeToggle />
      </div>

      <div className="auth-panel-body">
        <div className="auth-panel-inner">{children}</div>
      </div>
    </div>
  );
}

AuthSplit.Brief = BriefSidebar;
AuthSplit.Panel = AuthPanel;

export default AuthSplit;

/* ── Form vocabulary ─────────────────────────────────────────── */

const FIELD_NAMES = {
  email: 'Email',
  password: 'Password',
  company_name: 'Company name',
  company_size: 'Company size',
  num_employees: 'Employees',
  industry: 'Industry',
  region: 'Region',
};

/**
 * Turns an axios error into text that is always safe to render.
 * FastAPI 422s carry `detail` as an array of {loc, msg}; those become
 * per-field messages keyed by the last `loc` segment. Never returns an
 * object as the message (rendering one crashes React).
 *
 *   const { message, fields } = formatApiError(err, 'Sign-in failed');
 */
export function formatApiError(error, fallback = 'Something went wrong. Try again.') {
  const res = error?.response;
  if (!res) {
    if (error?.request || error?.message === 'Network Error') {
      return { message: 'The IntelliSecure API could not be reached. Check your connection and try again.', fields: {} };
    }
    return { message: fallback, fields: {} };
  }
  if (res.status === 429) {
    return { message: 'Too many attempts. Wait a minute, then try again.', fields: {} };
  }
  const detail = res.data?.detail;
  if (Array.isArray(detail)) {
    const fields = {};
    const messages = [];
    detail.forEach((d) => {
      const loc = Array.isArray(d?.loc) ? d.loc : [];
      const field = [...loc].reverse().find((p) => typeof p === 'string' && p !== 'body');
      const msg = String(d?.msg || 'Invalid value').replace(/^Value error,\s*/i, '');
      if (field && !fields[field]) fields[field] = msg;
      messages.push(field && FIELD_NAMES[field] ? `${FIELD_NAMES[field]}: ${msg}` : msg);
    });
    return {
      message: messages.length === 1 ? messages[0] : 'Some details need attention. Check the highlighted fields.',
      fields,
    };
  }
  if (typeof detail === 'string' && detail) return { message: detail, fields: {} };
  if (res.status >= 500) return { message: 'The server could not complete this request. Try again in a moment.', fields: {} };
  return { message: fallback, fields: {} };
}

/** ids for the hint / error text an input should reference. */
export function describedBy(id, { hint, error } = {}) {
  return [error && `${id}-error`, hint && `${id}-hint`].filter(Boolean).join(' ') || undefined;
}

/** Label + control + hint + inline error. The control gets its own id. */
export function AuthField({ id, label, hint, error, optional, labelAside, children }) {
  return (
    <div className="auth-field">
      <div className="auth-label-row">
        <label htmlFor={id} className="auth-label">
          {label}
          {optional && <span className="auth-optional"> (optional)</span>}
        </label>
        {labelAside}
      </div>
      {children}
      {hint && <p id={`${id}-hint`} className="auth-hint">{hint}</p>}
      {error && (
        <p id={`${id}-error`} className="auth-field-error">
          <AlertCircle aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

export const AuthInput = forwardRef(function AuthInput({ invalid, className = '', ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={`auth-input ${className}`}
      aria-invalid={invalid ? 'true' : undefined}
      {...rest}
    />
  );
});

/** Password input with a labelled show/hide toggle. */
export const PasswordInput = forwardRef(function PasswordInput({ invalid, ...rest }, ref) {
  const [show, setShow] = useState(false);
  return (
    <div className="auth-input-wrap">
      <AuthInput
        ref={ref}
        type={show ? 'text' : 'password'}
        invalid={invalid}
        className="auth-input--with-btn"
        spellCheck={false}
        autoCapitalize="off"
        {...rest}
      />
      <button
        type="button"
        className="auth-input-btn"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? 'Hide password' : 'Show password'}
        aria-pressed={show}
        aria-controls={rest.id}
      >
        {show ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
      </button>
    </div>
  );
});

/** Form-level error. Rendered in a persistent live region so it is announced. */
export function FormAlert({ id, message, children }) {
  return (
    <div aria-live="assertive" aria-atomic="true">
      {(message || children) && (
        <div id={id} className="auth-alert" role="alert">
          <AlertCircle aria-hidden="true" />
          <div>
            {message}
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

/** Full-width primary button with a busy state. */
export function AuthSubmit({ loading, loadingLabel = 'Working…', children, className = '', disabled, ...rest }) {
  return (
    <button
      type="submit"
      className={`auth-submit ${className}`}
      {...rest}
      disabled={loading || disabled}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <>
          <span className="auth-spinner" aria-hidden="true" />
          {loadingLabel}
        </>
      ) : children}
    </button>
  );
}
